import axios from 'axios';

import { getToken, notifyAuthLost } from '@gagovictor/shared-admin-core';

import { BFF_BASE_URL } from './authApi';

const originOf = (url: string): string | null => {
    try {
        return new URL(url, window.location.href).origin;
    } catch {
        return null;
    }
};

const OUR_ORIGINS = new Set([originOf(BFF_BASE_URL)]);

/**
 * Whether a request goes to the BFF. Compared by origin, not by
 * prefix, so "http://localhost:8002.evil.example" is not ours.
 */
export const isOurs = (url: string | undefined): boolean => {
    const origin = url ? originOf(url) : null;
    return origin !== null && OUR_ORIGINS.has(origin);
};

/**
 * The sign-in steps answer 401 for a wrong password or code: that is not a lost session. They run before there is a
 * session, so they carry no token either.
 */
const isLogin = (url: string | undefined): boolean =>
    !!url && /\/(login|mfa\/[a-z]+|sso\/google(\/[a-z]+)?)(\?|$)/.test(url);

let installed = false;

/**
 * Every axios call to the BFF carries the login token, and a
 * 401/403 answer from it signs the admin out. Requests to anywhere else are
 * left alone, so the token is never sent to a third party.
 */
export const installAuthInterceptors = (): void => {
    if (installed) {
        return;
    }
    installed = true;

    axios.interceptors.request.use((config) => {
        const token = getToken();
        if (token && isOurs(config.url) && !isLogin(config.url)) {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const url: string | undefined = error?.config?.url;
            if ((status === 401 || status === 403) && isOurs(url) && !isLogin(url)) {
                notifyAuthLost();
            }
            return Promise.reject(error);
        }
    );
};

/** fetch() for the few calls that do not use axios: same token, same sign-out. */
export const authFetch = async (
    url: string,
    init: RequestInit = {}
): Promise<Response> => {
    const headers = new Headers(init.headers);
    const token = getToken();
    if (token && isOurs(url)) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await fetch(url, { ...init, headers });
    if ((response.status === 401 || response.status === 403) && isOurs(url)) {
        notifyAuthLost();
    }
    return response;
};
